/**
 * API client for contractor app.
 * Uses mock auth header in development.
 */

import type { TaskStatus, LaborBucket } from 'scheduler-types';

// API base URL
// - Uses VITE_API_URL if set
// - Falls back to localhost:8787 in development
// - Falls back to same-origin (empty string) in production
const API_BASE =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? 'http://localhost:8787' : '');

/**
 * Contractor-scoped task response from API.
 * Note: customerName is intentionally NOT included per security requirements.
 */
export interface ContractorTask {
  id: string;
  lineItemId: string;
  laborBucket: LaborBucket;
  estimatedMinutesMin: number;
  estimatedMinutesMax: number;
  actualMinutes: number;
  status: TaskStatus;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  // SO number only - no customer name per contractor visibility rules
  gpSalesOrderNumber: string;
  // Part info for context
  partNumber: string;
  partDescription: string;
  // Skill info
  skillName: string;
  // Active time entry (if task is in progress)
  activeTimeEntry?: {
    id: string;
    startTime: string;
  };
}

export interface TasksResponse {
  data: ContractorTask[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ApiError {
  code: string;
  message: string;
  details?: Array<{ field?: string; message: string }>;
}

// Time entry types
export interface TimeEntry {
  id: string;
  clientEntryId: string;
  taskId: string;
  personId: string;
  startTime: string;
  endTime: string | null;
  durationMinutes: number | null;
  syncStatus: 'created' | 'duplicate' | 'conflict';
  createdAt: string;
  // Task info for display
  task?: {
    gpSalesOrderNumber: string;
    partNumber: string;
    laborBucket: string;
  };
}

export interface TimeEntriesResponse {
  data: TimeEntry[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// Response types for task actions
export interface StartTaskResponse {
  data: {
    taskId: string;
    timeEntryId: string;
    status: 'in_progress';
    startTime: string;
    serialNumber?: string; // For serialized builds
  };
}

export interface StopTaskResponse {
  data: {
    taskId: string;
    timeEntryId: string;
    status: 'ready' | 'in_progress';
    endTime: string;
    durationMinutes: number;
  };
}

export interface CompleteTaskResponse {
  data: {
    taskId: string;
    timeEntryId?: string;
    status: 'complete';
    endTime?: string;
    totalMinutes: number;
  };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}/api/v1${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add mock auth header for development
    if (import.meta.env.DEV) {
      (headers as Record<string, string>)['X-Mock-User-Role'] = 'contractor';
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      // Read response text first, then try to parse as JSON
      const responseText = await response.text();
      let errorCode = 'UNKNOWN_ERROR';
      let errorMessage = `API error: ${response.status}`;

      try {
        const body = JSON.parse(responseText);
        errorCode = body.error?.code ?? errorCode;
        errorMessage = body.error?.message ?? errorMessage;
      } catch {
        // JSON parse failed - use response text if available, otherwise statusText
        errorCode = 'NETWORK_ERROR';
        errorMessage = responseText || response.statusText || errorMessage;
      }

      throw new ApiRequestError(errorCode, errorMessage, response.status);
    }

    return response.json();
  }

  /**
   * Fetch tasks assigned to the current contractor.
   * Only returns tasks assigned to this user per API security.
   */
  async getMyTasks(options?: {
    status?: TaskStatus[];
    limit?: number;
    offset?: number;
  }): Promise<TasksResponse> {
    const params = new URLSearchParams();
    if (options?.status?.length) {
      params.set('status', options.status.join(','));
    }
    if (options?.limit) {
      params.set('limit', String(options.limit));
    }
    if (options?.offset) {
      params.set('offset', String(options.offset));
    }

    const query = params.toString();
    const endpoint = `/tasks${query ? `?${query}` : ''}`;

    return this.request<TasksResponse>(endpoint);
  }

  /**
   * Get a single task by ID.
   */
  async getTask(taskId: string): Promise<{ data: ContractorTask }> {
    return this.request(`/tasks/${taskId}`);
  }

  /**
   * Start working on a task.
   * Creates a new time entry and sets task to in_progress.
   */
  async startTask(taskId: string): Promise<StartTaskResponse> {
    return this.request(`/tasks/${taskId}/start`, {
      method: 'POST',
      body: JSON.stringify({
        deviceTimestamp: new Date().toISOString(),
      }),
    });
  }

  /**
   * Stop (pause) working on a task.
   * Closes the active time entry but keeps task ready for resume.
   */
  async stopTask(taskId: string): Promise<StopTaskResponse> {
    return this.request(`/tasks/${taskId}/stop`, {
      method: 'POST',
      body: JSON.stringify({
        deviceTimestamp: new Date().toISOString(),
      }),
    });
  }

  /**
   * Complete a task.
   * Closes the active time entry and sets task to complete.
   */
  async completeTask(taskId: string): Promise<CompleteTaskResponse> {
    return this.request(`/tasks/${taskId}/complete`, {
      method: 'POST',
      body: JSON.stringify({
        deviceTimestamp: new Date().toISOString(),
      }),
    });
  }

  /**
   * Fetch time entries for the current user.
   */
  async getMyTimeEntries(options?: {
    taskId?: string;
    limit?: number;
    offset?: number;
  }): Promise<TimeEntriesResponse> {
    const params = new URLSearchParams();
    if (options?.taskId) {
      params.set('taskId', options.taskId);
    }
    if (options?.limit) {
      params.set('limit', String(options.limit));
    }
    if (options?.offset) {
      params.set('offset', String(options.offset));
    }

    const query = params.toString();
    const endpoint = `/time-entries${query ? `?${query}` : ''}`;

    return this.request<TimeEntriesResponse>(endpoint);
  }
}

export class ApiRequestError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

// Singleton client instance
export const api = new ApiClient(API_BASE);
